// Relations between the leader's state and every actor (ARCHITECTURE §5). First match wins:
//
//   war        opposite sides of an active interstate War, or of a UCDP interstate dyad that year
//   ally       shared bloc with mutual_defense (both 'member'), or a mutual-defense Treaty
//   denounced  a comprehensive / sectoral / unfriendly-list / tariff Sanction, either direction
//   friend     UNGA ideal-point distance ≤ 0.5 (latest session year ≤ the date's year)
//   neutral    otherwise
//
// Readings of the rules, documented because the table leaves them open:
//   - A sanction by or on a bloc counts for each state that is a 'member' of it at the date
//     (EU restrictive measures bind every member state; Russia's 2014 embargo named the EU).
//   - Targeted sanctions never change the relation, so they do not block 'friend' either.
//   - Blocs are actors too: the leader's state is 'ally' of a mutual-defence bloc it belongs to.

import type { ActorId, Bloc, DateStr, Membership, Sanction, SourceId, Treaty, War, WorldState } from '../core/schema.ts';
import { inSpan, yearOf } from '../core/dates.ts';
import type { UcdpData } from '../data/types.ts';
import type { Grievance, RelationInfo } from './api.ts';
import type { ActorTable } from './actors.ts';
import { uniq, latestAt, endedBy } from './util.ts';
import { dyadIndex, dyadsAt, isActive, pairWindow, sidesAt, type DyadIndex } from './wars.ts';

/** ARCHITECTURE §5: UNGA ideal-point distance at or below this is 'friend'. */
export const FRIEND_MAX_DISTANCE = 0.5;

const DENOUNCE_SCOPES: ReadonlySet<Sanction['scope']> = new Set(['comprehensive', 'sectoral', 'unfriendly-list', 'tariff']);
const MEMBER = 'member';
const MUTUAL_DEFENSE = 'mutual-defense';
const INTERSTATE = 'interstate';

export interface Why {
  reason: string;
  sources: SourceId[];
}

export interface Diplo {
  actors: ActorTable;
  blocs: Map<ActorId, Bloc>;
  byBloc: Map<ActorId, Membership[]>;
  byMember: Map<ActorId, Membership[]>;
  treaties: Treaty[];
  sanctions: Sanction[];
  wars: War[];
  dyads: DyadIndex;
  unga: Record<string, Array<[number, number]>>;
  ungaSources: SourceId[];
}

function groupBy<T>(list: T[], key: (x: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const x of list) {
    const k = key(x);
    const arr = out.get(k) ?? [];
    arr.push(x);
    out.set(k, arr);
  }
  return out;
}

export function buildDiplo(
  world: WorldState, actors: ActorTable, ucdp: UcdpData, unga: Record<string, Array<[number, number]>>, ungaSources: SourceId[],
): Diplo {
  return {
    actors,
    blocs: new Map(world.blocs.map((b) => [b.id, b])),
    byBloc: groupBy(world.memberships, (m) => m.bloc),
    byMember: groupBy(world.memberships, (m) => m.member),
    treaties: world.treaties,
    sanctions: world.sanctions,
    wars: world.wars,
    dyads: dyadIndex(ucdp),
    unga,
    ungaSources,
  };
}

export function nameOf(d: Diplo, id: ActorId): string {
  const i = d.actors.byId.get(id);
  return (i !== undefined ? d.actors.list[i]?.name : undefined) ?? id;
}

/** Full members of a bloc at `date`. */
export function membersAt(d: Diplo, bloc: ActorId, date: DateStr): Membership[] {
  return (d.byBloc.get(bloc) ?? []).filter((m) => m.status === MEMBER && inSpan(m, date));
}

/** Blocs `id` is a full member of at `date`. */
export function blocsOf(d: Diplo, id: ActorId, date: DateStr): Membership[] {
  return (d.byMember.get(id) ?? []).filter((m) => m.status === MEMBER && inSpan(m, date));
}

function addOnce(map: Map<ActorId, Why>, id: ActorId, why: Why): void {
  if (!map.has(id)) {
    map.set(id, why);
  }
}

// ─── war ─────────────────────────────────────────────────────────────────────────────────

function opposite(self: ActorId, a: string[], b: string[]): string[] {
  if (a.includes(self)) {
    return b;
  }
  if (b.includes(self)) {
    return a;
  }
  return [];
}

/**
 * A UCDP year row with no episode start covers all of January 1 → December 31. When a curated war
 * of the same two sides starts later that year, its `from` is the real start, so the row says
 * nothing about the days before it (Iran and the USA: UCDP 2026, curated war from 2026-02-28).
 */
function curatedStartsLater(d: Diplo, self: ActorId, other: ActorId, year: number, date: DateStr): boolean {
  return d.wars.some((w) => {
    if (w.kind !== INTERSTATE || !opposite(self, w.sides.a, w.sides.b).includes(other)) {
      return false;
    }

    // The pair fights together from the later of their own spans (the USA joined Israel's war on Iran on 22 June).
    const start = pairWindow(w, self, other)?.from;
    return start !== undefined && yearOf(start) === year && date < start;
  });
}

export function enemiesOf(d: Diplo, self: ActorId, date: DateStr): Map<ActorId, Why> {
  const out = new Map<ActorId, Why>();
  for (const w of d.wars) {
    if (w.kind !== INTERSTATE || !isActive(w, date)) {
      continue;
    }
    const sides = sidesAt(w, date);
    const sources = uniq([...w.sources, ...w.status.sources]);
    for (const id of opposite(self, sides.a, sides.b)) {
      // A late party fights from its own span, not from the start of the war.
      const since = pairWindow(w, self, id)?.from ?? w.from;
      addOnce(out, id, { reason: `At war: ${w.name} (since ${since})`, sources });
    }
  }
  for (const y of dyadsAt(d.dyads, date)) {
    const why = { reason: `UCDP/PRIO records an interstate armed conflict in ${y.year}: ${y.name}`, sources: y.sources };
    for (const id of opposite(self, y.a, y.b)) {
      if (y.start === null && curatedStartsLater(d, self, id, y.year, date)) {
        continue;
      }
      addOnce(out, id, why);
    }
  }
  return out;
}

// ─── ally ────────────────────────────────────────────────────────────────────────────────

/** Allies through a shared mutual-defence bloc (and the blocs themselves). */
export function blocAllies(d: Diplo, self: ActorId, date: DateStr): Map<ActorId, Why> {
  const out = new Map<ActorId, Why>();
  for (const mine of blocsOf(d, self, date)) {
    const bloc = d.blocs.get(mine.bloc);
    if (!bloc?.mutual_defense) {
      continue;
    }
    addOnce(out, bloc.id, { reason: `Member of ${bloc.name}, a mutual-defence bloc`, sources: uniq([...bloc.sources, ...mine.sources]) });
    for (const m of membersAt(d, bloc.id, date)) {
      if (m.member === self) {
        continue;
      }
      const reason = `Both members of ${bloc.name}, a mutual-defence bloc`;
      addOnce(out, m.member, { reason, sources: uniq([...bloc.sources, ...mine.sources, ...m.sources]) });
    }
  }
  return out;
}

/** Allies through a mutual-defence Treaty in force. */
export function treatyAllies(d: Diplo, self: ActorId, date: DateStr): Map<ActorId, Why> {
  const out = new Map<ActorId, Why>();
  for (const t of d.treaties) {
    if (t.kind !== MUTUAL_DEFENSE || !inSpan(t, date) || !t.parties.includes(self)) {
      continue;
    }
    for (const p of t.parties) {
      if (p !== self) {
        addOnce(out, p, { reason: `Mutual-defence treaty: ${t.name}`, sources: t.sources });
      }
    }
  }
  return out;
}

/** Blocs first, then treaties; the first reason found for an ally is kept. */
export function alliesOf(d: Diplo, self: ActorId, date: DateStr): Map<ActorId, Why> {
  const out = blocAllies(d, self, date);
  for (const [id, why] of treatyAllies(d, self, date)) {
    addOnce(out, id, why);
  }
  return out;
}

// ─── denounced ───────────────────────────────────────────────────────────────────────────

/** A sanction side: the actor, plus the bloc's full members when it is a bloc. */
function sideOf(d: Diplo, id: ActorId, date: DateStr): ActorId[] {
  if (!d.blocs.has(id)) {
    return [id];
  }
  return [id, ...membersAt(d, id, date).map((m) => m.member)];
}

function sanctionWhy(d: Diplo, s: Sanction): Why {
  const reason = `Sanction by ${nameOf(d, s.by)} on ${nameOf(d, s.target)}: ${s.program} (${s.scope}, since ${s.from})`;
  return { reason, sources: s.sources };
}

export function denouncedBy(d: Diplo, self: ActorId, date: DateStr): Map<ActorId, Why> {
  const out = new Map<ActorId, Why>();
  for (const s of d.sanctions) {
    if (!DENOUNCE_SCOPES.has(s.scope) || !inSpan(s, date)) {
      continue;
    }
    const by = sideOf(d, s.by, date);
    const target = sideOf(d, s.target, date);
    const other = by.includes(self) ? target : target.includes(self) ? by : [];
    for (const id of other) {
      if (id !== self) {
        addOnce(out, id, sanctionWhy(d, s));
      }
    }
  }
  return out;
}

/** Sanctions and tariffs between two states, blocs expanded to their members. */
export function sanctionsBetween(d: Diplo, self: string, other: string, date: DateStr): Grievance[] {
  const out: Grievance[] = [];

  for (const s of d.sanctions) {
    if (s.from > date) {
      continue;
    }
    const by = sideOf(d, s.by, date);
    const target = sideOf(d, s.target, date);
    const forward = by.includes(self) && target.includes(other);
    const backward = by.includes(other) && target.includes(self);
    if (!forward && !backward) {
      continue;
    }
    out.push({
      by: forward ? self : other,
      target: forward ? other : self,
      kind: s.scope === 'tariff' ? 'tariff' : 'sanction',
      title: s.program,
      scope: s.scope,
      from: s.from,
      to: endedBy(s.to, date),
      sources: s.sources,
    });
  }
  return out;
}

// ─── friend ──────────────────────────────────────────────────────────────────────────────

function fmtPoint(id: ActorId, p: [number, number]): string {
  return `${id} ${p[1].toFixed(2)} (${p[0]})`;
}

/** UNGA closeness, or null when either side has no ideal point by that year. */
export function ungaWhy(d: Diplo, self: ActorId, id: ActorId, date: DateStr): Why | null {
  const year = yearOf(date);
  const a = latestAt(d.unga[self], year);
  const b = latestAt(d.unga[id], year);
  if (!a || !b) {
    return null;
  }
  const dist = Math.abs(a[1] - b[1]);
  if (dist > FRIEND_MAX_DISTANCE) {
    return null;
  }
  const reason = `UNGA ideal points ${fmtPoint(self, a)} and ${fmtPoint(id, b)}: distance ${dist.toFixed(2)} ≤ ${FRIEND_MAX_DISTANCE}`;
  return { reason, sources: d.ungaSources };
}

// ─── all together ────────────────────────────────────────────────────────────────────────

export interface RelContext {
  self: ActorId;
  date: DateStr;
  war: Map<ActorId, Why>;
  ally: Map<ActorId, Why>;
  denounced: Map<ActorId, Why>;
}

export function relContext(d: Diplo, self: ActorId, date: DateStr): RelContext {
  return {
    self, date, war: enemiesOf(d, self, date), ally: alliesOf(d, self, date), denounced: denouncedBy(d, self, date),
  };
}

const NEUTRAL_REASON = 'No active war, defence pact, relation-changing sanction or close UNGA voting at this date';

export function relationTo(d: Diplo, ctx: RelContext, id: ActorId): RelationInfo {
  if (id === ctx.self) {
    return { relation: 'self', reason: 'Your state', sources: [] };
  }
  const war = ctx.war.get(id);
  if (war) {
    return { relation: 'war', ...war };
  }
  const ally = ctx.ally.get(id);
  if (ally) {
    return { relation: 'ally', ...ally };
  }
  const bad = ctx.denounced.get(id);
  if (bad) {
    return { relation: 'denounced', ...bad };
  }
  const friend = ungaWhy(d, ctx.self, id, ctx.date);
  if (friend) {
    return { relation: 'friend', ...friend };
  }
  return { relation: 'neutral', reason: NEUTRAL_REASON, sources: [] };
}

/** Relation of every actor index to the leader's state. */
export function relationMap(d: Diplo, ctx: RelContext): Map<number, RelationInfo> {
  const out = new Map<number, RelationInfo>();
  d.actors.list.forEach((a, i) => {
    if (a) {
      out.set(i, relationTo(d, ctx, a.id));
    }
  });
  return out;
}
